import type { SubscriptionTier, Platform, SocialStatsPlatform, DealType } from '@/types/common';

export const TIER_LEVELS: Record<SubscriptionTier, number> = {
  starter: 0,
  essential: 1,
  pro: 2,
  manager: 3,
};

export const TIER_LABELS: Record<SubscriptionTier, string> = {
  // 'starter' is the internal pre-payment tier; per locked product rule the
  // word "Starter" must never appear in user-facing copy. Most call sites
  // gate via `tier !== 'starter' ? TIER_LABELS[tier] : 'your plan'`, but
  // this fallback label catches any ungated leak.
  starter: 'No plan',
  essential: 'Essential',
  pro: 'Pro',
  manager: 'Manager',
};

export const TIER_PRICES: Record<SubscriptionTier, number> = {
  starter: 0,
  essential: 49.99,
  pro: 99.99,
  manager: 149.99,
};

export const DEAL_LIMITS: Record<SubscriptionTier, number> = {
  starter: 3,
  essential: 15,
  pro: Infinity,
  manager: Infinity,
};

export const BRAND_LIMITS: Record<SubscriptionTier, number> = {
  starter: 3,
  essential: 25,
  pro: Infinity,
  manager: Infinity,
};

export const AI_MESSAGE_LIMITS: Record<SubscriptionTier, number> = {
  starter: 0,
  essential: 15,
  pro: 100,
  manager: 500,
};

// Threshold for marking pending performance_earnings as "stale" — surfaced
// by PendingAffiliateSummary when period_end is older than this many days.
// Fixed at v1; revisit configurability if real users surface different
// affiliate-network cycle lengths (some pay quarterly, some monthly).
//
// Comparison uses period_end (a YYYY-MM-DD date string) parsed as UTC
// midnight. The 60-day threshold is forgiving enough that timezone-
// boundary edge cases (a Pacific-time creator seeing "today" one day off
// from UTC) won't trip false positives.
export const STALE_PENDING_THRESHOLD_DAYS = 60;

export const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  twitter: 'Twitter/X',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  twitch: 'Twitch',
  podcast: 'Podcast',
  newsletter: 'Newsletter',
  blog: 'Blog',
  my_channel: 'MY CHANNEL',
  other: 'Other',
};

// ─── Social Stats platform set (single source of truth) ──────────────────
// The authoritative, ordered platform list for the Social Stats feature.
// Distinct from PLATFORM_LABELS / `Platform` above, which serve deals /
// goals / posts and must keep twitter, linkedin, pinterest, podcast,
// newsletter, blog. Do NOT fold these two together — adding the social
// platforms (kick, patreon, …) to PLATFORM_LABELS would leak them into the
// deal/goal platform pickers. Mirrored server-side in
// server/src/routes/social-stats.ts. Array order = display order.
export const SOCIAL_STATS_PLATFORMS: readonly SocialStatsPlatform[] = [
  'instagram',
  'tiktok',
  'twitch',
  'kick',
  'youtube',
  'patreon',
  'onlyfans',
  'my_channel',
  'snapchat',
  'spotify',
  'threads',
  'cameo',
  'other',
] as const;

export const SOCIAL_STATS_PLATFORM_LABELS: Record<SocialStatsPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  twitch: 'Twitch',
  kick: 'Kick',
  youtube: 'YouTube',
  patreon: 'Patreon',
  onlyfans: 'OnlyFans',
  my_channel: 'MY CHANNEL',
  snapchat: 'Snapchat',
  spotify: 'Spotify',
  threads: 'Threads',
  cameo: 'Cameo',
  other: 'Other',
};

export const DEAL_TYPE_LABELS: Record<DealType, string> = {
  sponsored_post: 'Sponsored Post',
  ugc: 'UGC',
  ambassador: 'Ambassador',
  affiliate: 'Affiliate',
  podcast: 'Podcast',
  speaking: 'Speaking',
  other: 'Other',
};

export const PAYMENT_TERMS_LABELS: Record<string, string> = {
  net_30: 'Net 30',
  net_60: 'Net 60',
  on_delivery: 'On Delivery',
  milestone: 'Milestone',
  custom: 'Custom',
};

export const CONTENT_TYPES = [
  'reel',
  'static_post',
  'story',
  'video',
  'blog',
  'podcast_mention',
  'newsletter',
  'ugc_video',
] as const;

export const CONTENT_TYPE_LABELS: Record<string, string> = {
  reel: 'Reel',
  static_post: 'Static Post',
  story: 'Story',
  video: 'Video',
  blog: 'Blog Post',
  podcast_mention: 'Podcast Mention',
  newsletter: 'Newsletter',
  ugc_video: 'UGC Video',
};

export const TIME_CATEGORIES = [
  'briefing',
  'creation',
  'editing',
  'revision',
  'communication',
  'other',
] as const;
